@if (isOpen()) {
  <div class="sp-overlay" (click)="onOverlayClick($event)" (keydown.escape)="close()" role="dialog" aria-label="Search" aria-modal="true">
    <div class="sp-panel">

      <!-- Search input row -->
      <div class="sp-input-row">
        <svg class="sp-search-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" aria-hidden="true">
          <circle cx="11" cy="11" r="7"/><path d="m20 20-3.5-3.5"/>
        </svg>
        <input
          #queryInput
          class="sp-input"
          type="text"
          [value]="query()"
          (input)="query.set($any($event.target).value)"
          (keydown)="onInputKeydown($event)"
          placeholder="Search solutions, providers, or keywords…"
          autocomplete="off"
          spellcheck="false"
          aria-label="Search"
        />
        @if (query()) {
          <button class="sp-clear-btn" (click)="clearQuery()" aria-label="Clear search">
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" aria-hidden="true">
              <path d="M6 6l12 12M18 6 6 18"/>
            </svg>
          </button>
        }
      </div>

      @if (query().trim(); as q) {

        <!-- Query suggestions -->
        @if (queryHints().length > 0) {
          <div class="sp-section">
            <div class="sp-section-label">Suggestions for '{{ q }}'</div>
            @for (hint of queryHints(); track hint; let i = $index) {
              <button class="sp-row" [class.sp-row--active]="i === 0" (click)="goToSearch(hint, 'hint')">
                <svg class="sp-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" aria-hidden="true">
                  <circle cx="11" cy="11" r="7"/><path d="m20 20-3.5-3.5"/>
                </svg>
                <span class="sp-row-text">
                  @for (part of highlight(hint, q); track $index) {
                    <span [class.sp-hl]="part.match">{{ part.text }}</span>
                  }
                </span>
                @if (i === 0) {
                  <svg class="sp-icon sp-icon--muted" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" aria-hidden="true">
                    <path d="m9 6 6 6-6 6"/>
                  </svg>
                }
              </button>
            }
          </div>
        }

        <!-- Category chips — "Likely in" -->
        @if (hasCategories()) {
          <div class="sp-section sp-section--border">
            <div class="sp-section-label">Likely in</div>
            <div class="sp-chips">
              @if (categories().tela > 0) {
                <button class="sp-chip sp-chip--tela" (click)="goToCategory('tela')">
                  <img src="assets/images/logos/SLB/tela.svg" width="11" height="11" alt="" aria-hidden="true" class="sp-chip-tela-logo" />
                  <span>Tela</span>
                  <span class="sp-chip-count">{{ categories().tela }}</span>
                </button>
              }
              @for (cat of categories().domains; track cat.name) {
                <button class="sp-chip" (click)="goToCategory('domain', cat.name)">
                  <svg width="11" height="11" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                    <path [attr.d]="domainIconPath(cat.name)" />
                  </svg>
                  <span class="sp-chip-prefix">Area:</span>
                  <span>{{ cat.name }}</span>
                  <span class="sp-chip-count">{{ cat.matches }}</span>
                </button>
              }
              @for (cat of categories().products; track cat.name) {
                <button class="sp-chip" (click)="goToCategory('product', cat.name)">
                  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
                    <rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/>
                    <rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/>
                  </svg>
                  <span class="sp-chip-prefix">Platform:</span>
                  <span>{{ cat.name }}</span>
                  <span class="sp-chip-count">{{ cat.matches }}</span>
                </button>
              }
              @for (cat of categories().providers; track cat.name) {
                <button class="sp-chip" (click)="goToCategory('provider', cat.name)">
                  <span class="sp-chip-at" aria-hidden="true">@</span>
                  <span class="sp-chip-prefix">By</span>
                  <span>{{ cat.name }}</span>
                  <span class="sp-chip-count">{{ cat.matches }}</span>
                </button>
              }
            </div>
          </div>
        }

        <!-- Direct solution matches -->
        @if (solutions().length > 0) {
          <div class="sp-section sp-section--border">
            <div class="sp-section-label">Solutions</div>
            @for (sol of solutions(); track sol.id) {
              <button class="sp-row sp-row--solution" (click)="goToProduct(sol, $index)">
                <div class="sp-logo-cell" aria-hidden="true">
                  <app-provider-logo [vendorName]="sol.provider" [height]="24" [maxWidth]="72" />
                </div>
                <div class="sp-sol-info">
                  <div class="sp-sol-name">
                    @for (part of highlight(sol.name, q); track $index) {
                      <span [class.sp-hl]="part.match">{{ part.text }}</span>
                    }
                  </div>
                  <div class="sp-sol-meta">
                    <span>{{ sol.provider }}</span>
                    @if (sol.domain) {
                      <span class="sp-dot">·</span><span>{{ sol.domain }}</span>
                    }
                    @if (sol.platform) {
                      <span class="sp-dot">·</span><span>{{ sol.platform }}</span>
                    }
                  </div>
                </div>
                <app-icon class="sp-icon sp-icon--faint" name="arrow-right-1" [size]="14" />
              </button>
            }
          </div>
        }

        <!-- No results -->
        @if (!hasContent()) {
          <div class="sp-no-results">
            <div class="sp-no-results-icon" aria-hidden="true">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6">
                <circle cx="11" cy="11" r="7"/><path d="m20 20-3.5-3.5"/>
              </svg>
            </div>
            <div class="sp-no-results-title">No results for "{{ q }}"</div>
            <div class="sp-no-results-sub">Try a broader term, or browse by domain</div>
          </div>
        }

      } @else {

        <!-- Idle state: recent + popular -->
        <div class="sp-section">
          <div class="sp-section-label">
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
              <circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/>
            </svg>
            Recent searches
          </div>
          @if (recentSearches().length > 0) {
            @for (r of recentSearches(); track r) {
              <div class="sp-row-wrap">
                <button class="sp-row" (click)="goToSearch(r, 'recent')">
                  <svg class="sp-icon sp-icon--muted" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" aria-hidden="true">
                    <circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/>
                  </svg>
                  <span class="sp-row-text">{{ r }}</span>
                </button>
                <button class="sp-remove-btn" (click)="removeRecentSearch($event, r)" aria-label="Remove from recent searches">
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" aria-hidden="true">
                    <path d="M6 6l12 12M18 6 6 18"/>
                  </svg>
                </button>
              </div>
            }
          } @else {
            <span class="sp-empty-recent">No recent searches</span>
          }
        </div>

        @if (recentlyViewed().length > 0) {
          <div class="sp-section sp-section--border">
            <div class="sp-section-label">
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                <circle cx="12" cy="12" r="3"/>
              </svg>
              Recently viewed
            </div>
            @for (item of recentlyViewed(); track item.id) {
              <button class="sp-row sp-row--solution" (click)="goToRecentlyViewed(item)">
                <div class="sp-logo-cell" aria-hidden="true">
                  <app-provider-logo [vendorName]="item.provider" [height]="24" [maxWidth]="72" />
                </div>
                <div class="sp-sol-info">
                  <div class="sp-sol-name">{{ item.name }}</div>
                  <div class="sp-sol-meta">
                    <span>{{ item.provider }}</span>
                    @if (item.domain) {
                      <span class="sp-dot">·</span><span>{{ item.domain }}</span>
                    }
                    @if (item.platform) {
                      <span class="sp-dot">·</span><span>{{ item.platform }}</span>
                    }
                  </div>
                </div>
                <app-icon class="sp-icon sp-icon--faint" name="arrow-right-1" [size]="14" />
              </button>
            }
          </div>
        }

        <div class="sp-section sp-section--border">
          <div class="sp-section-label">
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
              <path d="M22 12h-4l-3 9L9 3l-3 9H2"/>
            </svg>
            Popular searches
          </div>
          @for (p of popularSearches(); track p.q) {
            <button class="sp-row" (click)="goToSearch(p.q, 'popular')">
              <svg class="sp-icon sp-icon--blue" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" aria-hidden="true">
                <path d="M22 12h-4l-3 9L9 3l-3 9H2"/>
              </svg>
              <span class="sp-row-text">{{ p.label }}</span>
              <span class="sp-popular-count">{{ p.count }} results</span>
            </button>
          }
        </div>

      }

      <!-- Footer -->
      <div class="sp-footer">
        <span class="sp-keyhint"><kbd>↑↓</kbd> Navigate</span>
        <span class="sp-keyhint"><kbd>↵</kbd> Select</span>
        <span class="sp-keyhint"><kbd>esc</kbd> Close</span>
        <button class="sp-see-all" (click)="goToSearch()">Browse catalog →</button>
      </div>

    </div>
  </div>
}
